import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  resolveAptitudeTest,
  startAptitudeTest,
  submitAptitudeTest,
} from '../api/aptitudeTests';

function formatCountdown(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function optionEntries(question) {
  const entries = [
    ['A', question.optionA],
    ['B', question.optionB],
  ];
  if (question.optionC) entries.push(['C', question.optionC]);
  if (question.optionD) entries.push(['D', question.optionD]);
  return entries;
}

export default function PublicAptitudeTestPage() {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [clockOffsetMs, setClockOffsetMs] = useState(0);

  const reload = useCallback(async () => {
    setError('');
    const res = await resolveAptitudeTest(token);
    const data = res.data;
    if (data.serverNow) {
      setClockOffsetMs(new Date(data.serverNow).getTime() - Date.now());
    }
    setPayload(data);
    return data;
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await reload();
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'This test link is invalid.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reload, token]);

  useEffect(() => {
    if (!payload?.startedAt || payload.submitted) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [payload?.startedAt, payload?.submitted]);

  const remainingSeconds = useMemo(() => {
    if (!payload?.expiresAt) return null;
    const expiresMs = new Date(payload.expiresAt).getTime();
    const effectiveNow = nowMs + clockOffsetMs;
    return Math.floor((expiresMs - effectiveNow) / 1000);
  }, [payload?.expiresAt, nowMs, clockOffsetMs]);

  const timedOut = remainingSeconds != null && remainingSeconds <= 0 && !payload?.submitted;

  const handleStart = async () => {
    setStarting(true);
    setError('');
    try {
      const res = await startAptitudeTest(token);
      const data = res.data;
      if (data.serverNow) {
        setClockOffsetMs(new Date(data.serverNow).getTime() - Date.now());
      }
      setPayload(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not start the test.');
    } finally {
      setStarting(false);
    }
  };

  const handleSubmit = async () => {
    if (!payload?.questions?.length) return;
    const missing = payload.questions.some((q) => !answers[q.id]);
    if (missing) {
      setError('Please answer every question before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const answerList = payload.questions.map((q) => ({
        questionId: q.id,
        selectedOption: answers[q.id],
      }));
      const res = await submitAptitudeTest(token, answerList);
      setResult(res.data);
      await reload();
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) {
        setError('Time is up. Your answers could not be submitted.');
      } else {
        setError(err.response?.data?.message || err.message || 'Submit failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !payload) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="bg-white border border-red-200 rounded-xl p-8 max-w-md text-center space-y-3 shadow">
          <p className="text-red-700 font-semibold text-lg">Test unavailable</p>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const submitted = payload?.submitted || !!result;
  const passed = result?.passed ?? payload?.passed;
  const score = result?.scorePercentage ?? payload?.scorePercentage;
  const started = !!payload?.attemptId;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-10 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto card p-6 sm:p-8 space-y-6">
        <header className="border-b border-slate-200 pb-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Samastix Aptitude Test</p>
          <h1 className="text-2xl font-bold text-slate-900 mt-2">{payload?.testName}</h1>
          {payload?.companyName && (
            <p className="text-sm text-slate-500 mt-1">{payload.companyName}</p>
          )}
        </header>

        {!started && !submitted && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              Hi {payload?.candidateName || 'there'}, complete this multiple-choice test before your interview invite is sent.
            </p>
            <p className="text-sm text-slate-600">
              Time limit: <strong>{payload?.timeLimitMinutes} minutes</strong> once you start.
              Questions: <strong>{payload?.questions?.length ?? 0}</strong>
            </p>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="button"
              onClick={handleStart}
              disabled={starting}
              className="btn btn-primary w-full sm:w-auto"
            >
              {starting ? 'Starting…' : 'Start test'}
            </button>
          </div>
        )}

        {started && !submitted && (
          <>
            <div className="flex items-center justify-between rounded-lg bg-slate-100 border border-slate-200 px-4 py-3">
              <span className="text-sm font-medium text-slate-700">Time remaining</span>
              <span className={`text-lg font-mono font-bold ${timedOut ? 'text-red-600' : 'text-teal-700'}`}>
                {remainingSeconds != null ? formatCountdown(remainingSeconds) : '--:--'}
              </span>
            </div>
            {timedOut && (
              <p className="text-sm text-red-600">Time has expired. Submit is disabled.</p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="space-y-6">
              {(payload?.questions || []).map((q, idx) => (
                <fieldset key={q.id} className="border border-slate-200 rounded-lg p-4">
                  <legend className="text-sm font-semibold text-slate-800 px-1">
                    {idx + 1}. {q.questionText}
                  </legend>
                  <div className="mt-3 space-y-2">
                    {optionEntries(q).map(([key, label]) => (
                      <label
                        key={key}
                        className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name={`q-${q.id}`}
                          value={key}
                          checked={answers[q.id] === key}
                          disabled={timedOut || submitting}
                          onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: key }))}
                          className="mt-1"
                        />
                        <span className="text-sm text-slate-700">
                          <span className="font-semibold mr-2">{key}.</span>
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || timedOut}
              className="btn btn-primary w-full"
            >
              {submitting ? 'Submitting…' : 'Submit answers'}
            </button>
          </>
        )}

        {submitted && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center space-y-3">
            <p className="text-lg font-semibold text-slate-900">Test submitted</p>
            {score != null && (
              <p className="text-sm text-slate-600">Your score: <strong>{score}%</strong></p>
            )}
            {passed === true && (
              <p className="text-sm text-emerald-700">
                You passed. Check your email for interview details shortly.
              </p>
            )}
            {passed === false && (
              <p className="text-sm text-slate-600">
                You did not meet the pass threshold. The hiring team will review your result.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
